import Image from "next/image";

import cn from "classnames";

import { Link } from "@/components/link/link.component";
import { RatingStars } from "@/components/ratingStars/ratingStars.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import { STOREFRONT_HOME_PAGE } from "@/constants/page.constants";
import type { TwoColumnHighestRatedApp as TwoColumnHighestRatedAppData } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

function renderSectionImageColumn({
  sectionImageSrc,
  imageAlt,
}: {
  sectionImageSrc: string | null;
  imageAlt: string | undefined;
}) {
  return (
    <div
      className={cn(
        "bg-primary-100 relative min-w-0 overflow-hidden",
        sectionImageSrc
          ? "aspect-square w-full rounded-none p-6 lg:aspect-auto lg:h-full lg:min-h-[540px] lg:rounded-2xl lg:p-8"
          : "min-h-[280px] w-full rounded-none lg:h-full lg:min-h-[540px] lg:rounded-2xl",
      )}>
      {sectionImageSrc ? (
        <Image
          src={sectionImageSrc}
          alt={imageAlt ?? "ADT+ mobile app"}
          fill
          className="object-cover"
          sizes="(max-width: 1023px) 100vw, 48vw"
        />
      ) : (
        <div
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('/assets/adtplus-app/grid.png')",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        />
      )}
    </div>
  );
}

export const TwoColumnHighestRatedApp = ({
  block,
}: {
  block: TwoColumnHighestRatedAppData | null | undefined;
}) => {
  if (!block) return null;

  const sectionImageSrc = block.sectionImage?.asset
    ? urlFor(block.sectionImage).width(2046).height(1700).url()
    : null;

  const ctaHref =
    appendQueryString(block.ctaLink?.url, block.ctaLink?.queryString) ??
    STOREFRONT_HOME_PAGE;

  return (
    <section
      data-testid="highest-rated-app"
      className="overflow-x-hidden bg-white px-0 py-12 lg:px-8 lg:py-16">
      <div className="container mx-auto grid min-h-0 w-full grid-cols-1 gap-0 lg:grid-cols-2 lg:items-stretch lg:gap-10">
        {renderSectionImageColumn({
          sectionImageSrc,
          imageAlt: block.sectionImage?.alt,
        })}

        <div className="flex min-w-0 flex-col gap-6 px-8 pt-8 pb-12 lg:justify-center lg:gap-8 lg:px-0 lg:pt-0 lg:pb-0">
          <Text variant="h4" component="h4" className="text-greyscale-headline">
            {block.title}
          </Text>

          <Text variant="body4" className="text-greyscale-body">
            {block.description}
          </Text>

          <div className="flex flex-col gap-3">
            <Text
              variant="subtitle2"
              className="text-greyscale-headline font-bold">
              {block.ratingsHeading}
            </Text>
            <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
              <RatingStars
                rating={block.displayStarRating || 0}
                fillColor="text-secondary"
              />
              <Text variant="body2" className="text-greyscale-strength">
                {block.ratingSummary}
              </Text>
            </div>
          </div>

          <div>
            <Link
              href={ctaHref}
              secondary
              className="inline-flex px-8 py-3"
              data-testid="highest-rated-app-cta">
              {block.ctaLabel}
            </Link>
          </div>

          {block.footnotes && block.footnotes.length > 0 ? (
            <div className="text-greyscale-strength flex flex-col gap-2 [&_a]:underline">
              <RichText>{block.footnotes}</RichText>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
